import { ROOT_NODE, useEditor, useNode } from "@craftjs/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import debounce from "lodash/debounce";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input, Select } from "@/components/shared/forms";
import { EventOptionSelector } from "@/content-hub/editor/linked-event/event-option-selector";
import type {
  ContentHubEventSummary,
  PrimaryEventConfig,
} from "@/content-hub/event-summaries/types";
import { PRIMARY_EVENT_PAGE_PROP } from "@/content-hub/event-summaries/types";
import { mapLinkedEventOptionToContentHubEventSummaryRaw } from "@/content-hub/linked-events/helpers";
import type { LinkedEventOption } from "@/content-hub/linked-events/types";
import { getEventSummaryById } from "@/content-hub/store/selectors";
import { ColorLabels } from "@/content-hub/theme/constants";
import type { ButtonAction, ButtonActionType } from "@/content-hub/types";
import { useAppDispatch, useAppSelector } from "@/redux/store";

import type { ButtonProps } from "./button";
import { ButtonCustomStyleSettings } from "./button-custom-style-settings";

type ActionOption = { label: string; value: ButtonActionType };
type RootNodeProps = Record<string, unknown> & {
  [PRIMARY_EVENT_PAGE_PROP]?: PrimaryEventConfig;
};

const actionOptions: ActionOption[] = [
  { label: "preference-panel.none", value: "None" },
  { label: "preference-panel.section-of-page", value: "SectionOfPage" },
  { label: "preference-panel.external-link", value: "ExternalLink" },
  { label: "preference-panel.top-of-page", value: "TopOfPage" },
  { label: "preference-panel.bottom-of-page", value: "BottomOfPage" },
];

const DEFAULT_BUTTON_CTA_COLOR_SET: ButtonProps["colorSet"] = {
  background: ColorLabels["BRAND-PRIMARY"],
  text: ColorLabels["BRAND-SECONDARY"],
  border: ColorLabels["BRAND-PRIMARY"],
};

const isValidUrl = (rawUrl: string): boolean => {
  try {
    const url = new URL(rawUrl);
    return /^http(s?):/.test(url.protocol);
  } catch {
    return false;
  }
};

const throttleSetUrlError = debounce(
  (setUrlError: () => void) => setUrlError(),
  500
);

const getLinkedEventLabel = ({
  primaryEventExternalId,
  primaryEventSummary,
  t,
}: {
  primaryEventExternalId: string | null;
  primaryEventSummary: ContentHubEventSummary | null | undefined;
  t: (key: string) => string;
}): string => {
  if (!primaryEventExternalId) {
    return t("content-hub.linked-event.connect-event");
  }

  if (!primaryEventSummary) {
    return t("content-hub.linked-event.loading-event");
  }

  if (primaryEventSummary.status === "available") {
    return primaryEventSummary.title;
  }

  return (
    primaryEventSummary.title ?? t("content-hub.linked-event.unavailable-event")
  );
};

export const ButtonSettings = () => {
  const { t } = useTranslation();
  const flags = useFlags();
  const dispatch = useAppDispatch();
  const { contentHubLinkedEventPromotion: linkedEventFeatureEnabled } = flags;
  const {
    id: nodeId,
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));
  const { pageBlocks, primaryEventExternalId } = useEditor((state) => {
    const root = state.nodes[ROOT_NODE];
    const rootProps = root?.data?.props as RootNodeProps | undefined;
    const primaryEventConfig = rootProps?.[PRIMARY_EVENT_PAGE_PROP];
    const pageBlocks = root.data.nodes
      .map((nodeId) => {
        const node = state.nodes[nodeId];
        return node;
      })
      .filter(Boolean);

    return {
      pageBlocks,
      primaryEventExternalId: primaryEventConfig?.eventExternalId ?? null,
    };
  });

  const [urlError, setUrlError] = useState<string>("");

  const primaryEventSummary = useAppSelector((state) =>
    primaryEventExternalId
      ? getEventSummaryById(primaryEventExternalId)(state)
      : null
  );
  const localEventSummary = useAppSelector((state) =>
    props.action?.kind === "Event" && props.action.eventExternalId
      ? getEventSummaryById(props.action.eventExternalId)(state)
      : null
  );

  const handleActionChange = (kind: ButtonActionType) => {
    if (kind === "LinkedEvent" && !primaryEventExternalId) {
      dispatch({
        type: "[page editor] show linked event settings",
        payload: {
          setupRequest: {
            source: "button",
            nodeId,
          },
        },
      });
      setUrlError("");
      return;
    }

    setProp((props: ButtonProps) => {
      switch (kind) {
        case "ExternalLink":
          props.action = { kind, href: "" };
          break;
        case "SectionOfPage":
          props.action = { kind, nodeId: "" };
          break;
        case "LinkedEvent":
          props.action = { kind };
          break;
        case "Event":
          props.action = { kind, eventExternalId: null };
          break;
        default:
          props.action = { kind } as ButtonAction;
      }
    });
    setUrlError("");
  };

  const handleEventChange = (eventOption: LinkedEventOption) => {
    setProp((props: ButtonProps) => {
      props.action = {
        kind: "Event",
        eventExternalId: eventOption.id,
      };
    });

    dispatch({
      type: "[page editor] linked event option selected",
      payload: {
        eventExternalId: eventOption.id,
        title: eventOption.name,
      },
    });

    dispatch({
      type: "[page editor] fetch event summaries successful",
      payload: {
        eventSummaries: [
          mapLinkedEventOptionToContentHubEventSummaryRaw(eventOption),
        ],
        unavailableEventExternalIds: [],
      },
    });
  };

  const colorSet = (props.colorSet ??
    DEFAULT_BUTTON_CTA_COLOR_SET) as ButtonProps["colorSet"];
  const linkedEventLabel = getLinkedEventLabel({
    primaryEventExternalId,
    primaryEventSummary,
    t,
  });

  const options = useMemo(() => {
    const mappedOptions = actionOptions.map(({ label, value }) => ({
      label: t(label),
      value,
    }));

    if (!linkedEventFeatureEnabled) return mappedOptions;

    return [
      ...mappedOptions,
      {
        label: t("content-hub.linked-event.event"),
        value: "Event" as const,
      },
      {
        label: t("content-hub.linked-event.title"),
        value: "LinkedEvent" as const,
      },
    ];
  }, [linkedEventFeatureEnabled, t]);

  const sectionOfPageOptions = useMemo(() => {
    return pageBlocks.reduce((sections, block) => {
      sections.push({
        label: t(block.custom.settingsTitle),
        value: block.id,
      });

      return sections;
    }, []);
  }, [t, pageBlocks]);

  if (props.action?.kind === "EventPromotion") return null;

  return (
    <div className="flex flex-col gap-y-4 p-4">
      <Input
        data-testid="button-display-text-setting"
        label={t("preference-panel.display-text")}
        name={t("preference-panel.display-text")}
        value={props.text}
        onChange={(event) => {
          setProp(
            (props) => (props.text = (event.target as HTMLInputElement).value)
          );
        }}
      />
      <Select
        dataTestId="button-link-to-select"
        label={t("preference-panel.link-to")}
        value={props.action?.kind}
        options={options}
        selectedLabel={
          linkedEventFeatureEnabled && props.action?.kind === "LinkedEvent"
            ? linkedEventLabel
            : undefined
        }
        onChange={handleActionChange}
        name="linkto"
      />
      {linkedEventFeatureEnabled && props.action?.kind === "LinkedEvent" && (
        <p className="text-xs leading-[18px] text-gray-500">
          {t("content-hub.linked-event.button-description")}
        </p>
      )}
      {props.action?.kind === "ExternalLink" && (
        <Input
          data-testid="external-link-input"
          label={t("preference-panel.url")}
          name="link-to"
          value={props.action.href}
          error={urlError}
          onChange={(event) => {
            const url = (event.target as HTMLInputElement).value;
            const isValid = url === "" || isValidUrl(url);

            setProp(
              (props) =>
                (props.action = {
                  kind: "ExternalLink",
                  href: url,
                })
            );

            !isValid
              ? throttleSetUrlError(() =>
                  setUrlError(t("preference-panel.invalid-url"))
                )
              : setUrlError("");
          }}
          onBlur={(event) => {
            const url = (event.target as HTMLInputElement).value;

            if (isValidUrl(url)) return;

            if (!url) {
              setUrlError(t("preference-panel.invalid-url"));
              return;
            }

            setProp((props) => {
              props.action = {
                kind: "ExternalLink",
                href: "https://" + url,
              };
              return props;
            });
            throttleSetUrlError(() => setUrlError(""));
          }}
        />
      )}
      {props.action?.kind === "SectionOfPage" && (
        <Select
          label={t("preference-panel.link-to")}
          value={props.action?.nodeId}
          options={sectionOfPageOptions}
          placeholder={t("preference-panel.select-an-option")}
          onChange={(value) => {
            setProp(
              (props) =>
                (props.action = {
                  kind: "SectionOfPage",
                  nodeId: value,
                })
            );
          }}
          name="linkto"
        />
      )}
      {linkedEventFeatureEnabled && props.action?.kind === "Event" && (
        <EventOptionSelector
          id="content-hub-button-event"
          name="content-hub-button-event"
          dataTestId="button-event-option-input"
          label={t("content-hub.linked-event.event-name")}
          selectedEventExternalId={props.action.eventExternalId}
          selectedEventTitle={localEventSummary?.title}
          onChange={handleEventChange}
        />
      )}
      <ButtonCustomStyleSettings
        colorSet={colorSet}
        onChange={(updates) => {
          setProp((props: ButtonProps) => {
            props.colorSet = {
              ...colorSet,
              ...props.colorSet,
              ...updates,
            };
          });
        }}
        onReset={
          linkedEventFeatureEnabled
            ? () => {
                setProp((props: ButtonProps) => {
                  props.colorSet = { ...DEFAULT_BUTTON_CTA_COLOR_SET };
                });
              }
            : undefined
        }
      />
    </div>
  );
};
